import { newId } from './shed/store';

// Photos belong in IndexedDB, not localStorage. A successful save means the
// transaction committed; there is intentionally no volatile-memory fallback.
export const OFFLINE_FINDS_CHANGED = 'growdaily:offline-finds';
const DB_NAME = 'growdaily-offline-finds';
export const MAX_OFFLINE_FINDS = 100;

export function openOfflineFinds(idb = globalThis.indexedDB, name = DB_NAME) {
  return new Promise((resolve, reject) => {
    if (!idb) { reject(new Error('offline_storage')); return; }
    const request = idb.open(name, 1);
    let finished = false;
    const timer = setTimeout(() => { finished = true; reject(new Error('offline_storage')); }, 4000);
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore('drafts', { keyPath: 'id' });
      store.createIndex('owner', 'owner');
    };
    request.onerror = request.onblocked = () => { finished = true; clearTimeout(timer); reject(new Error('offline_storage')); };
    request.onsuccess = () => {
      clearTimeout(timer);
      const db = request.result;
      if (finished) { db.close(); return; }
      finished = true;
      db.onversionchange = () => db.close();
      resolve(db);
    };
  });
}

async function transaction(mode, run) {
  const db = await openOfflineFinds();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('drafts', mode);
    let result, reason = 'offline_storage';
    tx.oncomplete = () => {
      db.close();
      if (mode === 'readwrite') window.dispatchEvent(new Event(OFFLINE_FINDS_CHANGED));
      resolve(result);
    };
    tx.onabort = () => { db.close(); reject(new Error(reason)); };
    tx.onerror = () => {};
    run(tx.objectStore('drafts'), value => { result = value; }, message => { reason = message; tx.abort(); });
  });
}

export const offlineFinds = {
  list(owner) {
    return transaction('readonly', (store, done) => {
      const r = store.index('owner').getAll(owner);
      r.onsuccess = () => done(r.result.sort((a, b) => b.createdAt - a.createdAt));
    });
  },
  get(owner, id) {
    return transaction('readonly', (store, done) => {
      const r = store.get(id);
      r.onsuccess = () => done(r.result?.owner === owner ? r.result : null);
    });
  },
  save(owner, { id = newId(), files = [], payload = {}, createdAt = Date.now() }) {
    if (!owner || files.length > 3 || files.some(f => !(f instanceof Blob) || f.size > 8 * 1024 * 1024)) return Promise.reject(new Error('offline_photo_size'));
    return transaction('readwrite', (store, done, fail) => {
      const read = store.get(id);
      read.onsuccess = () => {
        if (read.result && read.result.owner !== owner) { fail('offline_owner'); return; }
        const count = store.index('owner').count(owner);
        count.onsuccess = () => {
          if (!read.result && count.result >= MAX_OFFLINE_FINDS) { fail('offline_full'); return; }
          const row = { id, owner, files, payload: { ...payload, share: false, share_location: false },
            createdAt: read.result?.createdAt || createdAt, updatedAt: Date.now() };
          store.put(row);
          done(row);
        };
      };
    });
  },
  remove(owner, id) {
    return transaction('readwrite', (store, done) => {
      const r = store.get(id);
      r.onsuccess = () => { if (r.result?.owner === owner) store.delete(id); done(); };
    });
  },
};

export function offlineSaveError(error, da) {
  if (error?.message === 'offline_full') return da ? 'Du har 100 lokale fund. Gem nogle på din konto eller fjern dem først.' : 'You have 100 local finds. Save some to your account or remove them first.';
  return da ? 'Kunne ikke gemme på telefonen. Behold billedet åbent, og kontrollér ledig lagerplads. Det er endnu ikke gemt.' : 'Could not save on this device. Keep the photo open and check free storage. It has not been saved yet.';
}
